import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom'; /* IMPORT CREATE PORTAL */
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import './Memories.css';

//const API_ORIGIN = 'http://localhost:5000';
const API_ORIGIN = 'http://localhost:5000';


function formatBytes(bytes) {
  if (bytes == null) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export default function Memories() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();

  const [files, setFiles] = useState([]);
  const [caption, setCaption] = useState('');
  const [uploadCategory, setUploadCategory] = useState('winter'); // default category
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false); // New modal state

  const [items, setItems] = useState([]);
  const [listBusy, setListBusy] = useState(false);

  const fileInputRef = useRef(null);

  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [likedPhotos, setLikedPhotos] = useState(new Set()); // Mock likes state

  // --- NEW STATES FOR ENHANCED FEATURES ---
  const [activeFilter, setActiveFilter] = useState('all');
  const [toastMsg, setToastMsg] = useState(null);
  
  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3500);
  };
  
  const CATEGORIES = ['all', 'winter', 'summer', 'hiking', 'interior', 'wildlife'];

  const mockStats = {
    photos: items.length || 142,
    contributors: new Set(items.map(i => i.user_name)).size || 45,
    likes: Array.from(likedPhotos).length + 840
  };

  const getPseudoCategory = (id) => {
    const cats = ['winter', 'summer', 'hiking', 'interior', 'wildlife'];
    // Deterministic pseudo-random category based on id
    const numId = typeof id === 'number' ? id : String(id).charCodeAt(0);
    return cats[(numId || 0) % cats.length];
  };

  const filteredItems = useMemo(() => {
    if (activeFilter === 'all') return items;
    return items.filter(it => getPseudoCategory(it.id) === activeFilter);
  }, [items, activeFilter]);

  const featuredItem = filteredItems.length > 0 ? filteredItems[0] : null;
  const galleryItems = filteredItems.length > 0 ? filteredItems.slice(1) : [];

  const canUpload = useMemo(() => !!user && !authLoading, [user, authLoading]);

  const refresh = async () => {
    setListBusy(true);
    setError('');
    try {
      const data = await api.listMemories();
      setItems(data.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setListBusy(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isUploadModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isUploadModalOpen]);

  const onChooseFiles = (e) => {
    const next = Array.from(e.target.files || []);
    setFiles(next);
  };

  const onUpload = async (e) => {
    e.preventDefault();
    setError('');

    if (!canUpload) {
      setError('Please sign in to upload your memories.');
      return;
    }

    if (!files.length) {
      setError('Choose at least one photo to upload.');
      return;
    }

    setBusy(true);
    try {
      // Pass the upload category to the api if supported
      await api.uploadMemories(files, caption, uploadCategory); 
      setFiles([]);
      setCaption('');
      setUploadCategory('winter');
      if (fileInputRef.current) fileInputRef.current.value = '';
      setIsUploadModalOpen(false); // Close modal on success
      await refresh();
      showToast('Memory uploaded successfully! 🎉');
    } catch (e2) {
      setError(e2.message);
    } finally {
      setBusy(false);
    }
  };

  const openLightbox = (index) => setLightboxIndex(index);
  
  const closeLightbox = () => setLightboxIndex(null);
  
  const handlePrevPhoto = (e) => {
    e.stopPropagation();
    setLightboxIndex(prev => (prev > 0 ? prev - 1 : items.length - 1));
  };
  
  const handleNextPhoto = (e) => {
    e.stopPropagation();
    setLightboxIndex(prev => (prev < items.length - 1 ? prev + 1 : 0));
  };
  
  useEffect(() => {
    if (lightboxIndex !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [lightboxIndex]);

  const toggleLike = (e, id) => {
    e.stopPropagation();
    setLikedPhotos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
        showToast('You liked a memory! ♥');
      }
      return newSet;
    });
  };

  return (
    <div className="memories page-enter">
      {/* GLOBAL TOAST NOTIFICATION */}
      <div className={`memories__toast ${toastMsg ? 'memories__toast--visible' : ''}`}>
        <div className="memories__toastIcon">
           <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
        </div>
        <span>{toastMsg}</span>
      </div>

      {/* --- RENDER MODAL VIA PORTAL TO FIX POSITIONING BUGS --- */}
      {isUploadModalOpen && createPortal(
        <div className="memories__modalOverlay" onClick={() => setIsUploadModalOpen(false)}>
          <div className="memories__modalContent" onClick={(e) => e.stopPropagation()}>
            <button className="memories__modalClose" onClick={() => setIsUploadModalOpen(false)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            
            <h2 className="memories__modalTitle">{t('memories.addJournal') || 'Share a Memory'}</h2>
            <p className="memories__modalSubtitle">{t('memories.uploadPrompt')}</p>
            
            {!user && !authLoading && (
              <div className="memories__notice">
                {t('memories.guestNotice')} <strong>{t('memories.signInUpload')}</strong> {t('memories.toUpload')}
              </div>
            )}

            <form onSubmit={onUpload} className="memories__form">
              <div className="memories__formGroup">
                <label className="memories__label">
                  <span className="memories__labelText">{t('memories.caption')}</span>
                  <input
                    className="memories__input"
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    placeholder={t('memories.captionPlaceholder')}
                    maxLength={120}
                  />
                </label>

                <label className="memories__label">
                  <span className="memories__labelText">Category</span>
                  <select
                    className="memories__input"
                    value={uploadCategory}
                    onChange={(e) => setUploadCategory(e.target.value)}
                  >
                    {CATEGORIES.filter(c => c !== 'all').map(cat => (
                      <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                    ))}
                  </select>
                </label>

                <label className="memories__label">
                  <span className="memories__labelText">{t('memories.photos')}</span>
                  <div className="memories__fileDropArea">
                    <input
                      ref={fileInputRef}
                      className="memories__fileInput"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={onChooseFiles}
                      disabled={!canUpload || busy}
                    />
                    <div className="memories__filePlaceholder">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5"/>
                        <polyline points="21 15 16 10 5 21"/>
                      </svg>
                      <span>{t('memories.dropPhotos')}</span>
                    </div>
                  </div>
                  <div className="memories__hint">{t('memories.supportFiles')} • {t('memories.maxImages')}</div>
                </label>
              </div>

              {files.length > 0 && (
                <div className="memories__queue">
                  {files.slice(0, 10).map((f) => (
                    <div key={f.name + f.size} className="memories__queueItem">
                      <span className="memories__queueName">{f.name}</span>
                      <span className="memories__queueMeta">{formatBytes(f.size)}</span>
                    </div>
                  ))}
                  {files.length > 10 && (
                    <div className="memories__queueMore">Only the first 10 files will be uploaded.</div>
                  )}
                </div>
              )}

              {error && <div className="memories__error">{error}</div>}

              <button
                type="submit"
                disabled={!canUpload || busy || files.length === 0}
                className="memories__uploadBtn"
              >
                {busy ? t('memories.publishing') || 'Se încarcă...' : t('memories.publishButton') || 'Publică Amintiri'}
              </button>
            </form>
          </div>
        </div>,
        document.body /* Portal target */
      )}

      {/* ─── ENHANCED HERO HEADER (Inspired by new UI design) ─── */}
      <header className="memories__hero">
        <div className="memories__heroBg">
          <div className="memories__heroGradient" />
          <div className="memories__heroPattern" />
        </div>

        {/* Floating particles like in other headers */}
        <div className="memories__heroParticles">
          {[...Array(20)].map((_, i) => (
            <span key={i} className="memories__heroParticle" style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 6}s`,
              animationDuration: `${5 + Math.random() * 7}s`,
              width: `${2 + Math.random() * 3}px`,
              height: `${2 + Math.random() * 3}px`,
            }} />
          ))}
        </div>

        <div className="memories__heroContent" data-reveal>
            <div className="memories__heroLabel">{t('memories.label')}</div>
            <h1 className="memories__heroTitle">{t('memories.title')}</h1>
            <p className="memories__heroSubtitle">
              {t('memories.subtitle')}
            </p>
        </div>
        
        {/* Wavy bottom divider match */}
        <svg className="memories__heroWave" viewBox="0 0 1440 120" preserveAspectRatio="none">
          <path d="M0,0 C240,100 480,120 720,80 C960,40 1200,60 1440,120 L1440,120 L0,120 Z" fill="var(--h-cream)"/>
        </svg>
      </header>

      <div className="memories__content" data-reveal>
        
        {/* --- STATS DASHBOARD --- */}
        <div className="memories__statsDash">
          <div className="memories__statCard">
            <h4 className="memories__statVal">{mockStats.photos}</h4>
            <p className="memories__statLabel">{t('memories.stats.photos') || 'Shared Memories'}</p>
            <div className="memories__statGlow"></div>
          </div>
          <div className="memories__statCard">
            <h4 className="memories__statVal">{mockStats.contributors}</h4>
            <p className="memories__statLabel">{t('memories.stats.contributors') || 'Happy Guests'}</p>
            <div className="memories__statGlow"></div>
          </div>
          <div className="memories__statCard">
            <h4 className="memories__statVal">{mockStats.likes}</h4>
            <p className="memories__statLabel">{t('memories.stats.likes') || 'Total Likes'}</p>
            <div className="memories__statGlow"></div>
          </div>
        </div>

        <div className="memories__uploadSection">
          <div className="memories__uploadHeader">
            <h2 className="memories__uploadTitle">{t('memories.addJournal')}</h2>
            <div className="memories__uploadSubtitle">{t('memories.uploadPrompt')}</div>
            <div className="memories__uploadLines"></div>
          </div>

          <button 
            className="memories__btn memories__btn--primary" 
            onClick={() => setIsUploadModalOpen(true)}
            style={{ margin: '0 auto', display: 'flex' }}
          >
            <span>{t('memories.uploadPrompt') || 'Upload a Memory'}</span>
          </button>
        </div>

        <div className="memories__gallery">
          <div className="memories__galleryHeader">
            <h2 className="memories__galleryTitle">{t('memories.latest')}</h2>
            <button
                className="memories__btnRefresh"
                type="button"
                onClick={refresh}
                disabled={listBusy}
                title="Refresh Gallery"
              >
              {listBusy ? '↻' : '↻'}
            </button>
          </div>

          {/* --- FILTER PILLS --- */}
          <div className="memories__filters">
            {CATEGORIES.map(cat => (
              <button 
                key={cat} 
                className={`memories__filterBtn ${activeFilter === cat ? 'memories__filterBtn--active' : ''}`}
                onClick={() => setActiveFilter(cat)}
              >
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </button>
            ))}
          </div>

          <div className="memories__masonry" aria-busy={listBusy ? 'true' : 'false'}>
            
            {listBusy && items.length === 0 && (
              <>
                {[...Array(6)].map((_, idx) => (
                  <div key={idx} className={`memories__skeleton memories__item--r${idx % 2 === 0 ? 1 : 2}`}>
                    <div className="memories__skeletonShimmer"></div>
                  </div>
                ))}
              </>
            )}

            {!listBusy && featuredItem && (
               <div className="memories__spotlight memories__item--c2 memories__item--r2" onClick={() => openLightbox(0)}>
                  <img
                    className="memories__spotlightImg"
                    src={featuredItem.url?.startsWith('http') ? featuredItem.url : `${API_ORIGIN}${featuredItem.url?.startsWith('/') ? featuredItem.url : `/${featuredItem.url}`}`}
                    alt="Featured Memory"
                  />
                  <div className="memories__spotlightOverlay">
                     <span className="memories__spotlightBadge">Featured</span>
                     <h3 className="memories__spotlightTitle">{featuredItem.caption || 'A Magical Moment'}</h3>
                     <p className="memories__spotlightAuthor">{featuredItem.user_name ? `Captured by ${featuredItem.user_name}` : 'A beautiful memory'}</p>
                  </div>
               </div>
            )}

            {!listBusy && galleryItems.map((it, i) => {
              // Ensure src starts with / or http
              const urlPath = it.url?.startsWith('/') ? it.url : `/${it.url}`;
              const src = it.url?.startsWith('http') ? it.url : `${API_ORIGIN}${urlPath}`;
              
              const cat = getPseudoCategory(it.id);
              
              // Refined masonry algorithm to ensure organic, fluid shapes
              const sizes = [
                { r: 1, c: 1 }, { r: 2, c: 2 }, { r: 1, c: 2 }, { r: 2, c: 1 }, { r: 1, c: 1 }
              ];
              const size = sizes[i % sizes.length];
              const isLiked = likedPhotos.has(it.id);
              
              return (
                <figure 
                  key={it.id} 
                  className={`memories__item memories__item--r${size.r} memories__item--c${size.c}`}
                  // Note: +1 for lightbox index because featuredItem is at index 0 
                  onClick={() => openLightbox(i + 1)}
                >
                  <span className={`memories__badge memories__badge--${cat}`}>{cat}</span>
                  <img
                    className="memories__img"
                    src={src}
                    alt={it.caption || 'Guest memory'}
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.classList.add('memories__img--broken');
                    }}
                  />
                  <figcaption className="memories__captionBox">
                    <div className="memories__captionOverlay">
                        {it.caption && <div className="memories__captionText">{it.caption}</div>}
                        {it.user_name && <div className="memories__captionBy">{t('memories.by')} {it.user_name}</div>}
                    </div>
                  </figcaption>
                  <div className="memories__itemActions">
                    <button 
                      className={`memories__actionBtn ${isLiked ? 'memories__actionBtn--liked' : ''}`}
                      onClick={(e) => toggleLike(e, it.id)}
                      title={t('memories.like')}
                    >
                      {isLiked ? '♥' : '♡'}
                    </button>
                    <button 
                      className="memories__actionBtn"
                      onClick={(e) => { e.stopPropagation(); openLightbox(i + 1); }}
                      title="Fullscreen"
                    >
                      ⤢
                    </button>
                  </div>
                </figure>
              );
            })}

            {!listBusy && items.length === 0 && (
              <div className="memories__empty">{t('memories.noUploads')}</div>
            )}
          </div>
        </div>
      </div>

      {lightboxIndex !== null && items[lightboxIndex] && (
        <div className="memories__lightbox" onClick={closeLightbox}>
          <button className="memories__lbClose" onClick={closeLightbox}>
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
          
          <button className="memories__lbNav memories__lbNav--prev" onClick={handlePrevPhoto}>
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
          </button>
          
          <div className="memories__lbContent" onClick={(e) => e.stopPropagation()}>
            <img 
              src={items[lightboxIndex].url?.startsWith('http') ? items[lightboxIndex].url : `${API_ORIGIN}${items[lightboxIndex].url?.startsWith('/') ? items[lightboxIndex].url : `/${items[lightboxIndex].url}`}`}
              alt={items[lightboxIndex].caption || 'Guest memory full'} 
              className="memories__lbImg"
            />
            <div className="memories__lbInfo">
               <div className="memories__lbMeta">
                 <div className="memories__lbCaption">{items[lightboxIndex].caption || 'Unknown'}</div>
                 <div className="memories__lbAuthor">{items[lightboxIndex].user_name ? `${t('memories.by')} ${items[lightboxIndex].user_name}` : ''}</div>
               </div>
               <div className="memories__lbActions">
                  <button 
                    className={`memories__actionBtn ${likedPhotos.has(items[lightboxIndex].id) ? 'memories__actionBtn--liked' : ''}`}
                    onClick={(e) => toggleLike(e, items[lightboxIndex].id)}
                  >
                    {likedPhotos.has(items[lightboxIndex].id) ? `♥ ${t('memories.liked')}` : `♡ ${t('memories.like')}`}
                  </button>
               </div>
            </div>
          </div>

          <button className="memories__lbNav memories__lbNav--next" onClick={handleNextPhoto}>
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
          </button>
        </div>
      )}
    </div>
  );
}
